import { ActionType } from 'typesafe-actions';
import * as actions from './filterActions';
import {
    CLEAR_ALL_FILTERS,
    INIT_FILTER_STATE,
    SET_DYNAMIC_FILTERS,
    UPDATE_DYNAMIC_FILTER,
    UPDATE_SEARCH_TERM,
    UPDATE_STARTS_WITH,
    SET_ACTIVE_PAGE,
    RESET_ACTIVE_PAGE,
} from './filterConstants';
import { HydratedCurriculumType } from '../Common/types/AppConfigTypes';

export type FilterAction = ActionType<typeof actions>;

type FilterStateMutable = {
    [slug: string]: CurriculumTypeFilters;
};

export type FilterState = Readonly<FilterStateMutable>;

export type CurriculumTypeFilters = {
    searchTerm: string;
    startsWith: string;
    dynamicFilters: DynamicFilterValues;
    activePageIndex: number;
};

export type DynamicFilterValues = {
    [dynamicFilterIndex: number]: string;
};

export const initialState = {};

export const defaultInitialState = {
    searchTerm: '',
    startsWith: 'A',
    dynamicFilters: {},
    activePageIndex: 0,
};

const initFilterState = (
    curriculumTypes: HydratedCurriculumType[],
    currentState: FilterState,
) => {
    const state: FilterStateMutable = {};

    curriculumTypes.forEach(ct => {
        state[ct.slug] = defaultInitialState;

        if (!currentState[ct.slug]) {
            return;
        }

        const { searchTerm, startsWith, dynamicFilters } = currentState[
            ct.slug
        ];

        if (searchTerm !== undefined) {
            state[ct.slug].searchTerm = searchTerm;
        }

        if (startsWith !== undefined) {
            state[ct.slug].startsWith = startsWith;
        }

        if (dynamicFilters !== undefined) {
            state[ct.slug].dynamicFilters = dynamicFilters;
        }
    });

    return state;
};

const updateStateForCurriculumType = (
    curriculumTypeSlug: string,
    newStatePartial: Partial<CurriculumTypeFilters>,
    state: FilterState,
): FilterState => {
    const updatedCTState = {
        ...state[curriculumTypeSlug],
        ...newStatePartial,
    };

    return {
        ...state,
        [curriculumTypeSlug]: updatedCTState,
    };
};

const updateDynamicFilter = (
    dynamicFilterIndex: number,
    value: string,
    dynamicFilters: DynamicFilterValues,
): DynamicFilterValues => {
    return {
        ...dynamicFilters,
        [dynamicFilterIndex]: value,
    };
};

export default (state: FilterState = initialState, action: FilterAction) => {
    switch (action.type) {
        case INIT_FILTER_STATE: {
            return initFilterState(action.payload, state);
        }
        case UPDATE_SEARCH_TERM: {
            const { searchTerm, activeCTSlug } = action.payload;

            return updateStateForCurriculumType(
                activeCTSlug,
                { searchTerm },
                state,
            );
        }
        case UPDATE_STARTS_WITH: {
            const { startsWith, activeCTSlug } = action.payload;

            return updateStateForCurriculumType(
                activeCTSlug,
                { startsWith },
                state,
            );
        }
        case SET_ACTIVE_PAGE: {
            const { activePageIndex, activeCTSlug } = action.payload;

            return updateStateForCurriculumType(
                activeCTSlug,
                { activePageIndex },
                state,
            );
        }
        case RESET_ACTIVE_PAGE: {
            const { activeCTSlug } = action.payload;

            return updateStateForCurriculumType(
                activeCTSlug,
                { activePageIndex: 0 },
                state,
            );
        }
        case UPDATE_DYNAMIC_FILTER: {
            const { dynamicFilterIndex, value, activeCTSlug } = action.payload;

            const updatedDynamicFilters = updateDynamicFilter(
                dynamicFilterIndex,
                value,
                state[activeCTSlug].dynamicFilters,
            );

            return updateStateForCurriculumType(
                activeCTSlug,
                { dynamicFilters: updatedDynamicFilters },
                state,
            );
        }
        case SET_DYNAMIC_FILTERS: {
            const { dynamicFilterValues, activeCTSlug } = action.payload;
            return updateStateForCurriculumType(
                activeCTSlug,
                { dynamicFilters: dynamicFilterValues }, // overwrites current filters
                state,
            );
        }
        case CLEAR_ALL_FILTERS: {
            return {
                ...state,
                [action.payload.activeCTSlug]: {
                    searchTerm: '',
                    startsWith: 'A',
                    dynamicFilters: {},
                },
            };
        }
        default:
            return state;
    }
};
